import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  useTheme,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { PhotoCamera, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ReactCrop, { Crop as ReactCropType } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ProfilePhotoUploadProps {
  existingImageUrl?: string | null;
  onPictureDeleted?: (isPictureDeleted: boolean) => void;
  onPhotoChange: (file: File | null, previewUrl?: string) => void;
}

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  existingImageUrl,
  onPhotoChange,
  onPictureDeleted,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [crop, setCrop] = useState<ReactCropType>({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  });
  const [completedCrop, setCompletedCrop] = useState<ReactCropType | null>(
    null
  );
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [openCropDialog, setOpenCropDialog] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const { t } = useTranslation();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSourceImage(reader.result as string);
        setOpenCropDialog(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeletePhoto = () => {
    onPhotoChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onPictureDeleted?.(true);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSaveCrop = () => {
    if (completedCrop && imgRef.current) {
      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const crop = completedCrop;

      canvas.width = crop.width;
      canvas.height = crop.height;

      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height
        );

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const previewUrl = URL.createObjectURL(blob);
              const file = new File([blob], 'profile.jpg', {
                type: 'image/jpeg',
              });
              onPhotoChange(file, previewUrl);
              onPictureDeleted?.(false);
            }
          },
          'image/jpeg',
          1
        );
      }
    }
    setOpenCropDialog(false);
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          width: 150,
          height: 150,
          borderRadius: '50%',
          border: `3px dashed ${theme.palette.primary.light}`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 10px ${theme.palette.primary.light}`,
          },
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {existingImageUrl ? (
          <Avatar
            src={existingImageUrl}
            sx={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              transition: 'opacity 0.3s ease',
              '&:hover': {
                opacity: 0.7,
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            <PhotoCamera sx={{ fontSize: 50, mb: 1 }} />
            <Typography variant="body2">{t('common.upload-photo')}</Typography>
          </Box>
        )}

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            backgroundColor: 'rgba(0,0,0,0.5)',
            '&:hover': {
              opacity: 1,
            },
          }}
        >
          <IconButton
            color="primary"
            onClick={triggerFileInput}
            sx={{
              backgroundColor: 'white',
              mr: 1,
              '&:hover': {
                backgroundColor: theme.palette.grey[200],
              },
            }}
          >
            <PhotoCamera />
          </IconButton>
          {existingImageUrl && (
            <IconButton
              color="error"
              onClick={handleDeletePhoto}
              sx={{
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.grey[200],
                },
              }}
            >
              <Delete />
            </IconButton>
          )}
        </Box>
      </Box>
      <Dialog open={openCropDialog} onClose={() => setOpenCropDialog(false)}>
        <DialogTitle>{t('common.crop-image')}</DialogTitle>
        <DialogContent>
          {sourceImage && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
            >
              <img
                ref={imgRef}
                src={sourceImage}
                alt="Source"
                style={{ maxHeight: '70vh' }}
              />
            </ReactCrop>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCropDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveCrop} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePhotoUpload;
